import { useEffect, useMemo, useRef, useState } from "react";
import { Copy, Eye, EyeOff, FolderDown, Loader2, Plus, RefreshCw, Save, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import CloudImagePicker from "@/components/admin/CloudImagePicker";
import {
  getUploadErrorMessage,
  normalizeAndValidateExternalImage,
  validateImageFile,
} from "@/lib/adminImageUtils";
import {
  deleteSecureClientGallery,
  createSecureClientGalleryAlbum,
  deleteSecureClientGalleryAlbum,
  generateShareToken,
  importDriveFolderMetadata,
  importSecureClientGalleryAlbum,
  listSecureClientGalleryAlbums,
  listSecureClientGalleries,
  normalizeClientGalleryCoverFrame,
  normalizeClientGalleryImages,
  saveSecureClientGallery,
  slugifyGallery,
  updateSecureClientGalleryAlbum,
  uploadClientGalleryImage,
} from "@/lib/clientGallerySecureApi";

const emptyForm = {
  id: "",
  title: "",
  slug: "",
  shareToken: "",
  accessPassword: "",
  status: "active",
  expiresAt: "",
  downloadAllowed: false,
  coverImage: "",
  coverPositionX: 50,
  coverPositionY: 36,
  coverScale: 1.08,
  gallery: [],
};

const coverFramePresets = [
  { label: "Top left", x: 18, y: 18 },
  { label: "Top", x: 50, y: 18 },
  { label: "Top right", x: 82, y: 18 },
  { label: "Left", x: 18, y: 50 },
  { label: "Center", x: 50, y: 50 },
  { label: "Right", x: 82, y: 50 },
  { label: "Bottom left", x: 18, y: 82 },
  { label: "Bottom", x: 50, y: 82 },
  { label: "Bottom right", x: 82, y: 82 },
];

function frameValue(value, min, max, fallback) {
  const number = Number(value);
  if (!Number.isFinite(number)) return fallback;
  return Math.min(max, Math.max(min, number));
}

export default function AdminClientGalleries() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(() => ({ ...emptyForm, shareToken: generateShareToken() }));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [albums, setAlbums] = useState([]);
  const [albumDraft, setAlbumDraft] = useState({ title: "", driveFolderUrlOrId: "", sortOrder: 0 });
  const [albumsLoading, setAlbumsLoading] = useState(false);
  const [albumBusyId, setAlbumBusyId] = useState("");
  const [driveFolderInput, setDriveFolderInput] = useState("");
  const [importingDrive, setImportingDrive] = useState(false);
  const [formMessage, setFormMessage] = useState(null);
  const fileRef = useRef(null);

  const privateLink = useMemo(() => {
    if (!form.shareToken || typeof window === "undefined") return "";
    return `https://www.mangalferafilms.com/client-gallery/g/${form.shareToken}`;
  }, [form.shareToken]);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await listSecureClientGalleries());
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const set = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const loadAlbums = async (eventId) => {
    if (!eventId) return setAlbums([]);
    setAlbumsLoading(true);
    try {
      setAlbums(await listSecureClientGalleryAlbums(eventId));
    } catch (error) {
      toast.error(error?.message || "Could not load gallery albums.");
    } finally {
      setAlbumsLoading(false);
    }
  };

  const edit = (item) => {
    setForm({
      id: item.id,
      title: item.title,
      slug: item.slug,
      shareToken: item.shareToken || generateShareToken(),
      accessPassword: item.accessPassword || "",
      status: item.status || "active",
      expiresAt: item.expiresAt ? item.expiresAt.slice(0, 10) : "",
      downloadAllowed: Boolean(item.downloadAllowed),
      coverImage: item.coverImage || "",
      ...normalizeClientGalleryCoverFrame(item),
      gallery: normalizeClientGalleryImages(item.gallery),
    });
    setAlbumDraft({ title: "", driveFolderUrlOrId: "", sortOrder: 0 });
    loadAlbums(item.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const reset = ({ keepMessage = false } = {}) => {
    setForm({ ...emptyForm, shareToken: generateShareToken() });
    setUrlInput("");
    setShowPin(false);
    setAlbums([]);
    setAlbumDraft({ title: "", driveFolderUrlOrId: "", sortOrder: 0 });
    if (!keepMessage) setFormMessage(null);
  };

  const save = async (event) => {
    event.preventDefault();
    setFormMessage(null);
    setSaving(true);
    try {
      const payload = {
        ...form,
        slug: slugifyGallery(form.slug || form.title),
        gallery: normalizeClientGalleryImages(form.gallery),
      };
      const successMessage = form.id ? "Client gallery updated successfully." : "Client gallery created successfully.";
      await saveSecureClientGallery(payload);
      toast.success(successMessage);
      setFormMessage({ type: "success", text: successMessage });
      reset({ keepMessage: true });
      await load();
    } catch (error) {
      const message = error?.message || "Could not save client gallery.";
      setFormMessage({ type: "error", text: message });
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete ${item.title}?`)) return;
    try {
      await deleteSecureClientGallery(item.id);
      toast.success("Client gallery deleted");
      if (form.id === item.id) reset();
      await load();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const copyLink = async (token = form.shareToken) => {
    const link = `https://www.mangalferafilms.com/client-gallery/g/${token}`;
    await navigator.clipboard.writeText(link);
    toast.success("Private share link copied");
  };

  const appendUrls = (urls) => {
    const next = [...normalizeClientGalleryImages(form.gallery), ...urls.map(String).map((value) => value.trim()).filter(Boolean)];
    set("gallery", [...new Set(next)]);
  };

  const uploadAlbum = async (event) => {
    const files = [...(event.target.files || [])].filter((file) => file.type.startsWith("image/"));
    if (!files.length) return;
    setUploading(true);
    try {
      files.forEach(validateImageFile);
      const urls = await Promise.all(files.map((file) => uploadClientGalleryImage(file, "client-galleries/gallery")));
      appendUrls(urls);
      toast.success(`${urls.length} photo${urls.length === 1 ? "" : "s"} uploaded`);
    } catch (error) {
      const message = getUploadErrorMessage(error);
      console.error("[ADMIN_CLIENT_GALLERY_UPLOAD_ERROR]", {
        fileCount: files.length,
        message,
        error,
      });
      toast.error(message);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = (index) => {
    set("gallery", normalizeClientGalleryImages(form.gallery).filter((_, photoIndex) => photoIndex !== index));
  };

  const addUrl = async () => {
    if (!urlInput.trim()) return;
    try {
      const imageUrl = await normalizeAndValidateExternalImage(urlInput);
      appendUrls([imageUrl]);
      setUrlInput("");
      toast.success("Image URL added");
    } catch (error) {
      const message = error?.message || "Could not load this image URL.";
      console.error("[ADMIN_CLIENT_GALLERY_URL_ERROR]", { input: urlInput, message });
      toast.error(message);
    }
  };

  const addAlbum = async () => {
    if (!form.id) {
      toast.error("Save the client gallery before adding folders.");
      return;
    }
    if (!albumDraft.title.trim()) {
      toast.error("Enter an album title.");
      return;
    }
    setAlbumBusyId("new");
    try {
      await createSecureClientGalleryAlbum(form.id, {
        title: albumDraft.title.trim(),
        driveFolderUrlOrId: albumDraft.driveFolderUrlOrId.trim(),
        sortOrder: Number(albumDraft.sortOrder) || albums.length,
        isActive: true,
      });
      setAlbumDraft({ title: "", driveFolderUrlOrId: "", sortOrder: albums.length + 1 });
      await loadAlbums(form.id);
      toast.success("Gallery album added.");
    } catch (error) {
      toast.error(error?.message || "Could not add gallery album.");
    } finally {
      setAlbumBusyId("");
    }
  };

  const importDriveFolder = async () => {
    if (!form.id || !driveFolderInput.trim()) return;
    setImportingDrive(true);
    try {
      const result = await importDriveFolderMetadata(form.id, driveFolderInput.trim());
      setDriveFolderInput("");
      await load();
      toast.success(`Imported ${result.importedImages || 0} photos and ${result.importedVideos || 0} videos.`);
    } catch (error) {
      toast.error(error?.message || "Could not import the Google Drive folder.");
    } finally {
      setImportingDrive(false);
    }
  };

  const updateAlbumField = (albumId, key, value) => {
    setAlbums((current) => current.map((album) => album.id === albumId ? { ...album, [key]: value } : album));
  };

  const saveAlbum = async (album) => {
    setAlbumBusyId(album.id);
    try {
      await updateSecureClientGalleryAlbum(form.id, album.id, {
        title: album.title,
        driveFolderUrlOrId: album.driveFolderId || album.driveFolderUrl,
        sortOrder: album.sortOrder,
        isActive: album.isActive,
        coverImage: album.coverImage,
      });
      await loadAlbums(form.id);
      toast.success(`${album.title} updated.`);
    } catch (error) {
      toast.error(error?.message || "Could not update album.");
    } finally {
      setAlbumBusyId("");
    }
  };

  const importAlbum = async (album) => {
    const folder = String(album.driveFolderId || album.driveFolderUrl || "").trim();
    if (!folder) return toast.error("Enter a Google Drive folder link or ID first.");
    setAlbumBusyId(`import-${album.id}`);
    try {
      const result = await importSecureClientGalleryAlbum(form.id, album.id, folder);
      await loadAlbums(form.id);
      await load();
      toast.success(`Imported ${result.importedImages || 0} photos and ${result.importedVideos || 0} videos into ${album.title}.`);
    } catch (error) {
      toast.error(error?.message || "Could not import this Drive folder.");
    } finally {
      setAlbumBusyId("");
    }
  };

  const removeAlbum = async (album) => {
    if (!window.confirm(`Delete ${album.title} and its imported metadata?`)) return;
    setAlbumBusyId(album.id);
    try {
      await deleteSecureClientGalleryAlbum(form.id, album.id);
      await loadAlbums(form.id);
      await load();
      toast.success("Gallery album deleted.");
    } catch (error) {
      toast.error(error?.message || "Could not delete album.");
    } finally {
      setAlbumBusyId("");
    }
  };

  const gallery = normalizeClientGalleryImages(form.gallery);
  const coverFrame = normalizeClientGalleryCoverFrame(form);
  const setCoverFrame = (key, value) => {
    const nextValue = key === "coverScale"
      ? frameValue(value, 1, 1.8, coverFrame.coverScale)
      : frameValue(value, 0, 100, coverFrame[key]);
    set(key, nextValue);
  };
  const applyCoverPreset = (preset) => {
    setForm((current) => ({
      ...current,
      coverPositionX: preset.x,
      coverPositionY: preset.y,
    }));
  };
  const resetCoverFrame = () => {
    setForm((current) => ({
      ...current,
      coverPositionX: 50,
      coverPositionY: 36,
      coverScale: 1.08,
    }));
  };

  return (
    <div className="p-8 md:p-10">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-5 mb-8">
        <div>
          <div className="eyebrow text-[#C99A45]">Secure Client Galleries</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2">Client Gallery Manager</h1>
          <p className="text-sm text-[#F7F1E8]/50 mt-3 max-w-2xl">
            Public visitors see only a private access form. Use this manager to create secure client links and control downloads.
          </p>
        </div>
        <button onClick={load} className="btn-gold-outline flex items-center gap-2 self-start" disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid xl:grid-cols-[1fr_0.9fr] gap-8">
        <form onSubmit={save} className="border border-[#C99A45]/15 bg-[#050505] p-5 md:p-7 space-y-5">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="eyebrow text-[#C99A45]">{form.id ? "Edit gallery" : "Create gallery"}</div>
              <h2 className="font-display text-3xl mt-2">{form.id ? form.title || "Selected Gallery" : "New Client Gallery"}</h2>
            </div>
            <button type="button" onClick={reset} className="btn-gold-outline flex items-center gap-2">
              <Plus size={14} /> New
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="block">
              <span className="eyebrow text-[10px] text-[#C99A45]/80">Client/Event name *</span>
              <input
                value={form.title}
                onChange={(event) => {
                  const title = event.target.value;
                  setForm((current) => ({
                    ...current,
                    title,
                    slug: current.id || current.slug ? current.slug : slugifyGallery(title),
                  }));
                }}
                className="admin-input w-full mt-2"
                required
              />
            </label>
            <label className="block">
              <span className="eyebrow text-[10px] text-[#C99A45]/80">Gallery slug/code *</span>
              <input value={form.slug} onChange={(event) => set("slug", slugifyGallery(event.target.value))} className="admin-input w-full mt-2" required />
            </label>
            <label className="block">
              <span className="eyebrow text-[10px] text-[#C99A45]/80">Password/PIN *</span>
              <div className="relative mt-2">
                <input
                  value={form.accessPassword}
                  onChange={(event) => set("accessPassword", event.target.value.replace(/[^A-Za-z0-9]/g, "").slice(0, 8))}
                  type={showPin ? "text" : "password"}
                  className="admin-input w-full pr-12"
                  minLength={4}
                  maxLength={8}
                  autoComplete="off"
                  required
                />
                <button type="button" onClick={() => setShowPin((value) => !value)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#F7F1E8]/55 hover:text-[#C99A45]" aria-label={showPin ? "Hide PIN" : "Show PIN"}>
                  {showPin ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </label>
            <label className="block">
              <span className="eyebrow text-[10px] text-[#C99A45]/80">Status</span>
              <select value={form.status} onChange={(event) => set("status", event.target.value)} className="admin-input w-full mt-2">
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </label>
            <label className="block">
              <span className="eyebrow text-[10px] text-[#C99A45]/80">Expiry date optional</span>
              <input value={form.expiresAt || ""} onChange={(event) => set("expiresAt", event.target.value)} type="date" className="admin-input w-full mt-2" />
            </label>
            <label className="flex items-center gap-3 border border-[#C99A45]/15 px-4 py-4 mt-6 md:mt-0">
              <input type="checkbox" checked={form.downloadAllowed} onChange={(event) => set("downloadAllowed", event.target.checked)} />
              <span className="text-sm text-[#F7F1E8]/70">Allow downloads</span>
            </label>
          </div>

          <div className="grid md:grid-cols-[1fr_1.4fr] gap-5">
            <CloudImagePicker
              value={form.coverImage}
              onChange={(value) => set("coverImage", value)}
              folder="client-galleries/covers"
              label="Cover image optional"
            />
            <div>
              <label className="eyebrow text-[10px] text-[#C99A45]/80 block mb-2">Private share token</label>
              <div className="flex gap-2">
                <input value={form.shareToken} onChange={(event) => set("shareToken", event.target.value.trim())} className="admin-input flex-1" required />
                <button type="button" onClick={() => set("shareToken", generateShareToken())} className="btn-gold-outline">Generate</button>
              </div>
              <div className="mt-3 flex gap-2">
                <input value={privateLink} readOnly className="admin-input flex-1 text-xs" />
                <button type="button" onClick={() => copyLink()} className="btn-gold-fill flex items-center gap-2">
                  <Copy size={13} /> Copy
                </button>
              </div>
            </div>
          </div>

          <div className="client-cover-framing-panel border border-[#C99A45]/15 bg-black p-4 md:p-5">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
              <div>
                <div className="eyebrow text-[#C99A45]">Hero banner framing</div>
                <p className="text-xs text-[#F7F1E8]/55 mt-2 max-w-2xl">
                  Set the client gallery cover like an Instagram crop preview. Move the subject and zoom until the live hero looks right.
                </p>
              </div>
              <button type="button" onClick={resetCoverFrame} className="btn-gold-outline text-[10px] self-start">
                Reset frame
              </button>
            </div>

            <div className="grid lg:grid-cols-[1.35fr_0.85fr] gap-5">
              <div className="client-cover-frame-preview">
                {form.coverImage ? (
                  <img
                    src={form.coverImage}
                    alt="Client gallery hero preview"
                    style={{
                      objectPosition: `${coverFrame.coverPositionX}% ${coverFrame.coverPositionY}%`,
                      transform: `scale(${coverFrame.coverScale})`,
                    }}
                  />
                ) : (
                  <div className="client-cover-frame-empty">Upload or paste a cover image first.</div>
                )}
                <div className="client-cover-frame-preview-shade" />
                <div className="client-cover-frame-preview-copy">
                  <span>Private Client Gallery</span>
                  <strong>{form.title || "Client Name"}</strong>
                </div>
              </div>

              <div className="space-y-4">
                <label className="block">
                  <span className="flex justify-between text-[11px] uppercase tracking-[0.18em] text-[#C99A45]">
                    Horizontal <b>{Math.round(coverFrame.coverPositionX)}%</b>
                  </span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={coverFrame.coverPositionX}
                    onChange={(event) => setCoverFrame("coverPositionX", event.target.value)}
                    className="admin-range mt-2"
                  />
                </label>
                <label className="block">
                  <span className="flex justify-between text-[11px] uppercase tracking-[0.18em] text-[#C99A45]">
                    Vertical <b>{Math.round(coverFrame.coverPositionY)}%</b>
                  </span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    step="1"
                    value={coverFrame.coverPositionY}
                    onChange={(event) => setCoverFrame("coverPositionY", event.target.value)}
                    className="admin-range mt-2"
                  />
                </label>
                <label className="block">
                  <span className="flex justify-between text-[11px] uppercase tracking-[0.18em] text-[#C99A45]">
                    Zoom <b>{coverFrame.coverScale.toFixed(2)}x</b>
                  </span>
                  <input
                    type="range"
                    min="1"
                    max="1.8"
                    step="0.01"
                    value={coverFrame.coverScale}
                    onChange={(event) => setCoverFrame("coverScale", event.target.value)}
                    className="admin-range mt-2"
                  />
                </label>

                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#C99A45] mb-2">Quick positions</div>
                  <div className="grid grid-cols-3 gap-2">
                    {coverFramePresets.map((preset) => (
                      <button
                        key={preset.label}
                        type="button"
                        onClick={() => applyCoverPreset(preset)}
                        className="client-cover-preset"
                      >
                        {preset.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-[#C99A45]/15 pt-5">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <div className="eyebrow text-[#C99A45]">Gallery photos</div>
                <div className="text-xs text-[#F7F1E8]/45 mt-1">{gallery.length} photo{gallery.length === 1 ? "" : "s"}</div>
              </div>
              <label className="btn-gold-outline flex items-center gap-2 cursor-pointer">
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? "Uploading..." : "Upload photos"}
                <input ref={fileRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" multiple className="hidden" onChange={uploadAlbum} disabled={uploading} />
              </label>
            </div>

            <div className="flex gap-2 mb-4">
              <input value={urlInput} onChange={(event) => setUrlInput(event.target.value)} placeholder="Paste one direct image URL or Google Drive image file link..." className="admin-input flex-1" />
              <button type="button" onClick={addUrl} disabled={uploading} className="btn-gold-outline">+ URL</button>
            </div>

            <div className="hidden">
              <div className="flex items-start gap-3">
                <FolderDown size={18} className="text-[#C99A45] mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="eyebrow text-[#C99A45]">Import Google Drive Folder</div>
                  <p className="text-xs text-[#F7F1E8]/60 mt-2 leading-relaxed">
                    Drive folder media must be shared as Anyone with the link → Viewer, otherwise clients cannot view photos/videos.
                    The service account must also be able to read the folder for import. Only metadata is saved; media files are not copied to Supabase Storage.
                  </p>
                  <div className="mt-4 flex flex-col md:flex-row gap-2">
                    <input
                      value={driveFolderInput}
                      onChange={(event) => setDriveFolderInput(event.target.value)}
                      placeholder="Paste Google Drive folder link or folder ID"
                      className="admin-input flex-1"
                      disabled={importingDrive}
                    />
                    <button
                      type="button"
                      onClick={importDriveFolder}
                      disabled={importingDrive || !form.id}
                      className="btn-gold-fill flex items-center justify-center gap-2 disabled:opacity-40"
                      title={!form.id ? "Save this gallery first" : "Import Drive metadata"}
                    >
                      {importingDrive ? <Loader2 size={14} className="animate-spin" /> : <FolderDown size={14} />}
                      {importingDrive ? "Importing..." : "Import Folder"}
                    </button>
                  </div>
                  {!form.id && <div className="text-[11px] text-[#C99A45]/70 mt-2">Save the gallery first, then import its Drive folder.</div>}
                </div>
              </div>
            </div>

            <div className="border border-[#C99A45]/30 bg-black p-4 md:p-5 mb-6 space-y-4">
              <div className="flex items-start gap-3">
                <FolderDown size={18} className="text-[#C99A45] mt-0.5 shrink-0" />
                <div>
                  <div className="eyebrow text-[#C99A45]">Gallery Folders / Albums</div>
                  <p className="text-xs text-[#F7F1E8]/60 mt-2 leading-relaxed">
                    Add Highlights, Mehendi, Wedding, Family, or another album. Photos and videos remain in Google Drive; only metadata is imported.
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-[1fr_1.5fr_90px_auto] gap-2">
                <input value={albumDraft.title} onChange={(event) => setAlbumDraft((current) => ({ ...current, title: event.target.value }))} placeholder="Album title" className="admin-input" />
                <input value={albumDraft.driveFolderUrlOrId} onChange={(event) => setAlbumDraft((current) => ({ ...current, driveFolderUrlOrId: event.target.value }))} placeholder="Google Drive folder link or ID" className="admin-input" />
                <input value={albumDraft.sortOrder} onChange={(event) => setAlbumDraft((current) => ({ ...current, sortOrder: event.target.value }))} type="number" min="0" className="admin-input" aria-label="Album display order" />
                <button type="button" onClick={addAlbum} disabled={!form.id || albumBusyId === "new"} className="btn-gold-fill flex items-center justify-center gap-2 disabled:opacity-40">
                  {albumBusyId === "new" ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Add Folder
                </button>
              </div>
              {!form.id && <div className="text-[11px] text-[#C99A45]/70">Save the gallery first, then add its albums.</div>}

              {albumsLoading ? (
                <div className="py-5 flex items-center gap-2 text-xs text-[#C99A45]"><Loader2 size={14} className="animate-spin" /> Loading albums...</div>
              ) : albums.length ? (
                <div className="space-y-3">
                  {albums.map((album) => (
                    <div key={album.id} className="border border-[#C99A45]/15 p-3 grid lg:grid-cols-[1fr_1.4fr_80px_1fr_auto] gap-2 items-center">
                      <input value={album.title} onChange={(event) => updateAlbumField(album.id, "title", event.target.value)} className="admin-input" aria-label="Album title" />
                      <input
                        value={album.driveFolderId || album.driveFolderUrl || ""}
                        onChange={(event) => {
                          updateAlbumField(album.id, "driveFolderId", event.target.value);
                          updateAlbumField(album.id, "driveFolderUrl", "");
                        }}
                        placeholder="Drive folder link or ID"
                        className="admin-input"
                      />
                      <input value={album.sortOrder} onChange={(event) => updateAlbumField(album.id, "sortOrder", event.target.value)} type="number" min="0" className="admin-input" aria-label="Display order" />
                      <input value={album.coverImage || ""} onChange={(event) => updateAlbumField(album.id, "coverImage", event.target.value)} placeholder="Optional cover image URL" className="admin-input" />
                      <div className="flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-2 text-[11px] text-[#F7F1E8]/65">
                          <input type="checkbox" checked={album.isActive} onChange={(event) => updateAlbumField(album.id, "isActive", event.target.checked)} /> Active
                        </label>
                        <span className="text-[10px] text-[#C99A45]">{album.photoCount} media</span>
                        <button type="button" onClick={() => saveAlbum(album)} disabled={Boolean(albumBusyId)} className="btn-gold-outline text-[10px]">Save</button>
                        <button type="button" onClick={() => importAlbum(album)} disabled={Boolean(albumBusyId)} className="btn-gold-fill flex items-center gap-1 text-[10px]">
                          {albumBusyId === `import-${album.id}` ? <Loader2 size={12} className="animate-spin" /> : <FolderDown size={12} />} {album.photoCount ? "Re-import" : "Import Folder"}
                        </button>
                        <button type="button" onClick={() => removeAlbum(album)} disabled={Boolean(albumBusyId)} className="border border-red-500/35 p-2 text-red-200 hover:bg-red-500/10" aria-label={`Delete ${album.title}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : form.id ? (
                <div className="border border-dashed border-[#C99A45]/20 p-5 text-center text-xs text-[#F7F1E8]/45">No albums yet. Add the first folder above.</div>
              ) : null}
            </div>

            {gallery.length ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-h-[520px] overflow-auto pr-1">
                {gallery.map((photo, index) => (
                  <div key={`${photo}-${index}`} className="relative border border-[#C99A45]/15 bg-black">
                    <img src={photo} alt="" className="w-full aspect-square object-cover" loading="lazy" />
                    <button type="button" onClick={() => removePhoto(index)} className="absolute right-1 top-1 bg-black/75 p-1 text-red-200 hover:text-red-400">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border border-dashed border-[#C99A45]/20 p-8 text-center text-sm text-[#F7F1E8]/45">
                No photos added yet.
              </div>
            )}
          </div>

          {formMessage && (
            <div className={`border px-4 py-3 text-sm ${formMessage.type === "success" ? "border-green-500/30 text-green-200" : "border-red-500/30 text-red-200"}`}>
              {formMessage.text}
            </div>
          )}

          <button disabled={saving || uploading} className="btn-gold-fill flex items-center gap-2">
            {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
            {saving ? "Saving..." : "Save Gallery"}
          </button>
        </form>

        <section className="border border-[#C99A45]/15 bg-[#050505]">
          <div className="p-5 border-b border-[#C99A45]/15">
            <div className="eyebrow text-[#C99A45]">All galleries</div>
          </div>
          {loading ? (
            <div className="p-8 text-[#C99A45] flex items-center gap-2"><Loader2 className="animate-spin" /> Loading galleries...</div>
          ) : items.length ? (
            <div className="divide-y divide-[#C99A45]/10">
              {items.map((item) => (
                <article key={item.id} className="p-5 hover:bg-[#C99A45]/5 transition">
                  <div className="flex gap-4">
                    <div className="w-24 h-20 bg-black border border-[#C99A45]/15 overflow-hidden shrink-0">
                      {item.coverImage ? <img src={item.coverImage} alt="" className="w-full h-full object-cover" /> : null}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-display text-2xl">{item.title}</h3>
                          <div className="text-xs text-[#F7F1E8]/45 mt-1">/{item.slug}</div>
                        </div>
                        <span className={`eyebrow text-[9px] px-2 py-1 border ${item.status === "active" ? "text-green-200 border-green-500/30" : "text-red-200 border-red-500/30"}`}>
                          {item.status}
                        </span>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2 text-[10px] uppercase tracking-[0.18em] text-[#F7F1E8]/45">
                        <span>{item.photoCount} media</span>
                        <span>Downloads {item.downloadAllowed ? "on" : "off"}</span>
                        <span>{item.updatedAt ? new Date(item.updatedAt).toLocaleDateString() : "Not updated"}</span>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <button onClick={() => edit(item)} className="btn-gold-outline text-[10px]">Edit</button>
                        <button onClick={() => copyLink(item.shareToken)} className="btn-gold-outline text-[10px] flex items-center gap-1"><Copy size={12} /> Link</button>
                        <button onClick={() => remove(item)} className="border border-red-500/40 text-red-200 px-3 py-2 text-[10px] uppercase tracking-[0.18em] hover:bg-red-500/10 flex items-center gap-1">
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="p-8 text-[#F7F1E8]/45">No client galleries yet.</div>
          )}
        </section>
      </div>
    </div>
  );
}
